// src/components/ValidationDiffTable.tsx
import "./ValidationDiffTable.css";
import React, { useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { selectValidationDiffItems } from "./validationDiffSelectors";
import { buildValidationDiffGrid } from "./buildValidationDiffGrid";

import type { RoomValidationDiffItem } from "@backend/src/shared/schemas/room/roomValidationSchema";

interface ValidationDiffTableProps {
  diffItems: RoomValidationDiffItem[];
  renderComponentAction?: (compCode: string) => React.ReactNode;
  standardCountHeaderName: string;
  occurrenceLayout?: boolean;
  filter?: { search: string; onlyDeviations: boolean };
  onFilterChange?: (filter: { search?: string; onlyDeviations?: boolean }) => void;
}

export const ValidationDiffTable: React.FC<ValidationDiffTableProps> = ({
  diffItems,
  renderComponentAction,
  standardCountHeaderName,
  occurrenceLayout = false,
  filter, onFilterChange,
}) => {
  const [localQuery, setQuery] = useState("");
  const [localOnlyDeviations, setOnlyDeviations] = useState(false);
  const query = filter?.search ?? localQuery;
  const onlyDeviations = filter?.onlyDeviations ?? localOnlyDeviations;
  const visibleItems = selectValidationDiffItems({ diffItems, onlyDeviations });
  const { defaultColDef, columnDefs, gridOptions } = useMemo(
    () => buildValidationDiffGrid({
      occurrenceLayout,
      countHeader: standardCountHeaderName,
      renderComponentAction,
    }),
    [occurrenceLayout, standardCountHeaderName, renderComponentAction]
  );

  return (
    <div
      style={{
        width: "100%",
        minHeight: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div className="validation-search-row">
        <input
          aria-label="Søk i komponenter"
          value={query}
          onChange={(e) => onFilterChange ? onFilterChange({ search: e.currentTarget.value }) : setQuery(e.currentTarget.value)}
          placeholder="Søk i komponentkode eller navn…"
          style={{
            width: "100%",
            height: 32,
            padding: "0 10px",
            borderRadius: 6,
            border: "1px solid rgba(0,0,0,0.15)",
            outline: "none",
            boxSizing: "border-box",
          }}
        />

        {occurrenceLayout && (
          <label className="occurrence-filter">
            <input
              type="checkbox"
              checked={onlyDeviations}
              onChange={(event) => onFilterChange ? onFilterChange({ onlyDeviations: event.target.checked }) : setOnlyDeviations(event.target.checked)}
            />
            Vis bare avvik
          </label>
        )}
      </div>

      {/* Viktigt: flex:1 + minHeight:0 => grid får scrollen och fyller resterande höjd */}
      <div
        className={`ag-theme-quartz ${occurrenceLayout ? "occurrence-grid" : ""}`}
        style={{ flex: 1, minHeight: 0, width: "100%" }}
      >
        <AgGridReact<RoomValidationDiffItem>
          rowData={visibleItems}
          quickFilterText={query}
          overlayNoRowsTemplate="<span>Ingen treff med valgte filtre</span>"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          sideBar={false}
          animateRows={false}
        />
      </div>
    </div>
  );
};
